/**
 * Long-form guide pages.
 *
 * Deliberately separate from FEATURE_PAGES: those are the product's features
 * and drive the navbar "Features" dropdown, the homepage grid and the footer's
 * Product column. A guide is not a feature — it is a page that answers a
 * question and then points at the tool.
 *
 * Kept as a list for the same reason FEATURE_PAGES is: the footer column and
 * the auth middleware's public-route set both read from here, so a new guide
 * cannot be added and then silently sit behind the session check.
 */
export interface GuidePage {
  id: string
  path: string
  /** i18n key of the link label — short, not the full SEO title. */
  titleKey: string
}

export const GUIDE_PAGES: GuidePage[] = [
  {
    id: 'write-resume',
    path: '/how-to-write-a-resume',
    titleKey: 'resumeGuide.heading',
  },
  {
    id: 'resume-length',
    path: '/how-long-should-a-resume-be',
    titleKey: 'resumeLengthGuide.heading',
  },
  {
    id: 'ats-template',
    path: '/ats-friendly-resume-template',
    titleKey: 'atsTemplateGuide.heading',
  },
  {
    id: 'prepare-interview',
    path: '/how-to-prepare-for-an-interview',
    titleKey: 'interviewGuide.heading',
  },
  // Hubs of the pages generated from data; each one links to its own pages.
  {
    id: 'resume-examples',
    path: '/resume-examples',
    titleKey: 'resumeExamples.hub.heading',
  },
  {
    id: 'resume-synonyms',
    path: '/resume-synonyms',
    titleKey: 'resumeSynonyms.hub.heading',
  },
  {
    id: 'alternatives',
    path: '/alternatives',
    titleKey: 'alternatives.hub.heading',
  },
  // /ai-cover-letter is both a feature page and the cover letter guide, so it
  // stays in FEATURE_PAGES and is not repeated here.
]

export const GUIDE_PAGE_PATHS = GUIDE_PAGES.map(page => page.path)

/**
 * The guide that owns a blog category's main query. An article in that
 * category links up to it, so the blog feeds the guide instead of competing
 * with it. `labelKey` is the link text.
 */
export const GUIDE_FOR_BLOG_CATEGORY: Record<string, { path: string, labelKey: string }> = {
  'job-interview': { path: '/how-to-prepare-for-an-interview', labelKey: 'interviewGuide.heading' },
  'resume-cv': { path: '/how-to-write-a-resume', labelKey: 'resumeGuide.heading' },
  'cover-letter': { path: '/ai-cover-letter', labelKey: 'coverLetterGuide.heading' },
}

/**
 * The seven steps of /how-to-write-a-resume, in rendered order.
 *
 * Lives here rather than in the component because the page builds the HowTo
 * structured data from the same list the component renders — if the two drifted
 * apart, the markup would promise steps that are not on the page.
 */
export const RESUME_STEP_IDS = [
  'gather',
  'layout',
  'header',
  'summary',
  'experience',
  'rest',
  'finish',
] as const

export type ResumeStepId = (typeof RESUME_STEP_IDS)[number]

/**
 * The seven steps of /how-to-prepare-for-an-interview, in rendered order.
 * Same reason as RESUME_STEP_IDS: the page's HowTo graph is built from it.
 */
export const INTERVIEW_STEP_IDS = [
  'research',
  'posting',
  'cv',
  'stories',
  'practice',
  'questions',
  'logistics',
] as const

export type InterviewStepId = (typeof INTERVIEW_STEP_IDS)[number]
